import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@artosera/ui';
import { sante } from '../lib/api.js';
import { formaterDate, formaterDuree } from '../lib/format.js';
import pageStyles from '../styles/page.module.css';

export function Health(): JSX.Element {
  const { t } = useTranslation('console');
  const requete = useQuery({
    queryKey: ['console', 'sante'],
    queryFn: sante,
    refetchInterval: 30_000,
  });

  const donnees = requete.data;

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('sante.titre')}</h1>
          <p>{t('sante.texte')}</p>
        </div>
        {donnees ? (
          <Badge tone={donnees.status === 'ok' ? 'available' : 'sold'}>
            {t(`sante.statut.${donnees.status}`)}
          </Badge>
        ) : null}
      </div>

      {requete.isLoading || !donnees ? (
        <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>
      ) : (
        <>
          <dl className={pageStyles.grilleStats}>
            <div className={pageStyles.stat}>
              <dt>{t('sante.version')}</dt>
              <dd>{donnees.version}</dd>
            </div>
            <div className={pageStyles.stat}>
              <dt>{t('sante.environnement')}</dt>
              <dd>{donnees.environment}</dd>
            </div>
            <div className={pageStyles.stat}>
              <dt>{t('sante.disponibiliteDepuis')}</dt>
              <dd>{formaterDuree(donnees.uptime * 1000)}</dd>
            </div>
          </dl>

          <div className={pageStyles.panneau}>
            <div className={pageStyles.sectionTitre}>
              <h2>{t('sante.plateforme.titre')}</h2>
              <Badge tone={donnees.plateforme.ok ? 'available' : 'sold'}>
                {donnees.plateforme.ok ? t('sante.statut.ok') : t('sante.plateforme.hors')}
              </Badge>
            </div>
            <dl className={pageStyles.grilleStats}>
              <div className={pageStyles.stat}>
                <dt>{t('sante.plateforme.base')}</dt>
                <dd>{donnees.plateforme.base ?? '—'}</dd>
              </div>
              <div className={pageStyles.stat}>
                <dt>{t('sante.plateforme.ping')}</dt>
                <dd>
                  {donnees.plateforme.pingMs === null ? '—' : `${donnees.plateforme.pingMs} ms`}
                </dd>
              </div>
            </dl>
          </div>

          <div className={pageStyles.panneau}>
            <div className={pageStyles.sectionTitre}>
              <h2>{t('sante.galeries.titre')}</h2>
            </div>
            <dl className={pageStyles.grilleStats}>
              <div className={pageStyles.stat}>
                <dt>{t('sante.galeries.connexions')}</dt>
                <dd>{donnees.galeries.connexionsOuvertes}</dd>
              </div>
              <div className={pageStyles.stat}>
                <dt>{t('sante.galeries.inactivite')}</dt>
                <dd>{formaterDuree(donnees.galeries.inactiviteMaximaleMs)}</dd>
              </div>
            </dl>
            {donnees.galeries.bases.length === 0 ? (
              <p className={pageStyles.videEtat}>{t('sante.galeries.aucune')}</p>
            ) : (
              <p>{donnees.galeries.bases.join(', ')}</p>
            )}
          </div>

          <div className={pageStyles.panneau}>
            <div className={pageStyles.sectionTitre}>
              <h2>{t('sante.migration.titre')}</h2>
            </div>
            {donnees.derniereMigration ? (
              <p>
                {donnees.derniereMigration.id} —{' '}
                {formaterDate(donnees.derniereMigration.appliqueeLe, '—')}
              </p>
            ) : (
              <p className={pageStyles.videEtat}>{t('sante.migration.aucune')}</p>
            )}
          </div>

          <p className={pageStyles.videEtat}>
            {t('sante.verifieLe', { date: formaterDate(donnees.verifieLe, '—') })}
          </p>
        </>
      )}
    </section>
  );
}
